import { useTranslate } from "@opensea/next-translate"
import React from "react"
import { graphql, useFragment } from "react-relay"
import { PaymentAssetMedia } from "@/components/common/media/PaymentAssetMedia"
import type { PaymentAssetTransfer_metadata$key } from "@/lib/graphql/__generated__/PaymentAssetTransfer_metadata.graphql"
import { BigNumber, bn } from "@/lib/helpers/numberUtils"
import { TxItem } from "../components/TxItem.react"
import { useTransactionUrl } from "../hooks/useTransactionUrl"
import type { TxState } from "../types"

type PaymentAssetTransferProps = {
  metadata: PaymentAssetTransfer_metadata$key
  state: TxState
  transactionHash: string | undefined
  nonce?: number
  createdDate?: string
}

export function PaymentAssetTransfer({
  metadata: metadataKey,
  state,
  transactionHash,
  nonce,
  createdDate,
}: PaymentAssetTransferProps) {
  const t = useTranslate("transactions")
  const transactionUrl = useTransactionUrl(transactionHash)

  const { sentAssetQuantities, receivedAssetQuantities } =
    useFragment<PaymentAssetTransfer_metadata$key>(
      graphql`
        fragment PaymentAssetTransfer_metadata on TransactionMetadataType {
          sentAssetQuantities {
            asset {
              symbol
              decimals
              usdSpotPrice
              ...PaymentAssetMedia_asset
            }
            quantity
          }
          receivedAssetQuantities {
            asset {
              symbol
              decimals
              usdSpotPrice
              ...PaymentAssetMedia_asset
            }
            quantity
          }
        }
      `,
      metadataKey,
    )

  // Transfer either has one sent asset or one received asset
  const getTransferType = () => {
    if (
      sentAssetQuantities.length > 0 &&
      receivedAssetQuantities.length === 0
    ) {
      return "sent"
    }
    if (
      sentAssetQuantities.length === 0 &&
      receivedAssetQuantities.length > 0
    ) {
      return "received"
    }
    return "transfer"
  }
  const transferType = getTransferType()

  const getDisplayAssetQuantities = () => {
    switch (transferType) {
      case "sent":
        return sentAssetQuantities
      case "received":
        return receivedAssetQuantities
      default:
        return []
    }
  }
  const displayAssetQuantities = getDisplayAssetQuantities()

  const displayAsset = displayAssetQuantities.length
    ? displayAssetQuantities[0].asset
    : undefined

  const isComplete = transactionHash && state !== "PENDING"
  const isPending = state === "PENDING"

  const getTitle = () => {
    switch (transferType) {
      case "sent":
        return t("txItem.paymentAssetTransfer.sent.title", "Sent")
      case "received":
        return t("txItem.paymentAssetTransfer.received.title", "Received")
      default:
        return t("txItem.paymentAssetTransfer.transfer.title", "Transfer")
    }
  }

  const renderEndEnhancer = () => {
    if (
      isComplete &&
      // if we support it, it must have a symbol
      displayAsset?.symbol &&
      (transferType === "sent" || transferType === "received")
    ) {
      const displayAssetQuantity = bn(
        BigNumber.sum(...displayAssetQuantities.map(aq => aq.quantity)),
        displayAsset.decimals,
      )
      return (
        <TxItem.Value
          direction={transferType}
          symbol={displayAsset.symbol}
          unit={displayAssetQuantity}
          usd={bn(displayAssetQuantity).times(displayAsset.usdSpotPrice ?? 1)}
        />
      )
    }
    if (isPending) {
      return (
        <TxItem.Pending
          createdDate={createdDate}
          href={transactionUrl}
          nonce={nonce}
        />
      )
    }
    return <TxItem.Requested />
  }

  return (
    <TxItem href={isPending ? undefined : transactionUrl} target="_blank">
      <TxItem.Avatar state={state}>
        <PaymentAssetMedia asset={displayAsset} />
      </TxItem.Avatar>
      <TxItem.Content>
        <TxItem.Title>{getTitle()}</TxItem.Title>
        <TxItem.Description>
          {displayAsset?.symbol ??
            t(
              "txItem.paymentAssetTransfer.unrecognizedToken",
              "Unrecognized Token",
            )}
        </TxItem.Description>
      </TxItem.Content>
      {renderEndEnhancer()}
    </TxItem>
  )
}
